import { ChangeEvent, Fragment, useCallback, useState } from 'react';
import ReactCropperImage from 'react-easy-crop';
import { ButtonPrimary, ButtonSecondary } from '@components/button';
import { getCroppedImg } from '@components/drag-and-drop/src/utils';
import { Dialog, Transition, TransitionChild } from '@headlessui/react';

interface Props {
  image: {
    file: File | null;
    src: string;
  };
  open: boolean;
  setOpen: (value: boolean) => void;
  onCancelCrop: () => void;
  onSaveCrop: (image: { file: File; src: string }) => void;
  translations: {
    cancel: string;
    save: string;
    zoom: string;
    rotate: string;
  };
  aspectRatio?: number;
}

interface Area {
  width: number;
  height: number;
  x: number;
  y: number;
}

export const Cropper = ({
  open,
  setOpen,
  image: { file, src },
  onSaveCrop,
  onCancelCrop,
  translations,
  aspectRatio = 16 / 9,
}: Props) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  // TODO: check unknown type
  const onCropComplete = useCallback((_: unknown, area: Area) => {
    // eslint-disable-next-line
    setCroppedAreaPixels(area);
  }, []);

  const saveCroppedImage = async () => {
    if (!file) {
      return;
    }
    const { type: imgType } = file;
    if (!croppedAreaPixels) {
      return;
    }

    const croppedImage = await getCroppedImg(
      src,
      // eslint-disable-next-line
      imgType,
      croppedAreaPixels,
      rotation
    );
    onSaveCrop(croppedImage as { file: File; src: string });
  };

  const handleZoom = (e: ChangeEvent<HTMLInputElement>) => {
    setZoom(parseFloat(e.target.value));
  };

  const handleRotate = (e: ChangeEvent<HTMLInputElement>) => {
    setRotation(parseFloat(e.target.value));
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as='div' static className='fixed z-50 inset-0 overflow-y-auto' open={open} onClose={setOpen}>
        <div className='flex items-center justify-center min-h-screen p-4 text-center sm:block sm:p-0'>
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'>
            <div className='fixed inset-0 backdrop-blur-sm bg-transparent-30 transition-opacity' />
          </TransitionChild>

          <span className='hidden sm:inline-block sm:align-middle sm:h-screen' aria-hidden='true'>
            &#8203;
          </span>

          <div className='inline-block align-middle bg-surface-primary rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all w-full sm:my-8 sm:max-w-sm sm:w-full lg:max-w-2xl sm:p-6'>
            <div className='relative w-full h-96'>
              <ReactCropperImage
                image={src}
                crop={crop}
                rotation={rotation}
                zoom={zoom}
                aspect={aspectRatio}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>
            <div className='mt-4 sm:mt-6'>
              <div className='grid grid-rows-2 md:grid-rows-1 md:grid-cols-2 gap-y-4 gap-x-8'>
                <div className='flex flex-row space-x-4'>
                  <label className='text-primary' htmlFor='zoom'>
                    {translations.zoom}
                  </label>
                  <input
                    id='zoom'
                    name='zoom'
                    className='text-primary'
                    value={zoom}
                    type='range'
                    onChange={handleZoom}
                    min='1'
                    max='3'
                    step='0.1'
                  />
                </div>

                <div className='flex flex-row space-x-4'>
                  <label className='text-primary' htmlFor='rotate'>
                    {translations.rotate}
                  </label>
                  <input
                    id='rotate'
                    name='rotate'
                    value={rotation}
                    type='range'
                    onChange={handleRotate}
                    min={0}
                    max={360}
                    step={1}
                  />
                </div>
              </div>

              <div className='flex justify-end mt-4 sm:mt-6 gap-4'>
                <ButtonSecondary type='button' data-testid='cancel-event-image' onClick={onCancelCrop}>
                  {translations.cancel}
                </ButtonSecondary>

                <ButtonPrimary type='button' data-testid='save-event-image' onClick={saveCroppedImage}>
                  {translations.save}
                </ButtonPrimary>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
